// LT-314 — State hook for the first-run checklist. Persistence is
// localStorage-only for now per the ticket's "if available; else small
// additive migration" clause — the ticket opens the door to a backend
// flag but the user-preferences table doesn't currently exist, and we'd
// rather ship the UX now and fold the backend sync into a follow-up
// than block on a migration.
//
// Each task has a boolean "completed" flag. Tasks auto-tick when the
// task is completed elsewhere (phone set on profile, contacts exist,
// sequences exist, team size > 1, billing viewed). Manual dismissal of
// the whole panel persists as `dismissed=true` and hides the panel
// permanently for that user.
//
// Storage key is scoped per-user so a shared machine with multiple
// accounts doesn't cross-contaminate state.
import { useCallback, useEffect, useMemo, useState } from 'react';

export type ChecklistTaskId =
  | 'connect_phone'
  | 'import_contacts'
  | 'create_campaign'
  | 'invite_teammate'
  | 'review_billing';

export interface ChecklistTask {
  id: ChecklistTaskId;
  label: string;
  description: string;
  to: string;
  completed: boolean;
}

export interface AutoCompleteSignals {
  phoneSet: boolean;
  hasContacts: boolean;
  hasCampaigns: boolean;
  teamSizeGreaterThanOne: boolean;
  billingViewed: boolean;
}

const STORAGE_KEY_PREFIX = 'onboardingChecklist:v1';

interface StoredState {
  dismissed: boolean;
  completions: Partial<Record<ChecklistTaskId, boolean>>;
}

function storageKey(userId: string | null | undefined): string {
  return `${STORAGE_KEY_PREFIX}:${userId ?? 'anon'}`;
}

function readStored(userId: string | null | undefined): StoredState {
  if (typeof window === 'undefined') return { dismissed: false, completions: {} };
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return { dismissed: false, completions: {} };
    const parsed = JSON.parse(raw) as StoredState;
    return {
      dismissed: Boolean(parsed?.dismissed),
      completions: parsed?.completions ?? {},
    };
  } catch {
    return { dismissed: false, completions: {} };
  }
}

function writeStored(userId: string | null | undefined, next: StoredState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // localStorage full / disabled — silently ignore. The worst case is
    // the checklist reappears on next reload, which is harmless.
  }
}

export interface UseOnboardingChecklistResult {
  tasks: ChecklistTask[];
  dismissed: boolean;
  visible: boolean;
  completeCount: number;
  dismiss: () => void;
  /**
   * Manually mark a task as complete. Auto-completion via signals also
   * updates the persisted state — this is for actions that don't map
   * cleanly to a signal (e.g. "Review billing" is marked when the user
   * lands on /settings/billing).
   */
  markCompleted: (id: ChecklistTaskId) => void;
}

export function useOnboardingChecklist(
  userId: string | null | undefined,
  signals: AutoCompleteSignals,
): UseOnboardingChecklistResult {
  const [state, setState] = useState<StoredState>(() => readStored(userId));

  // Re-read when the user identity changes (login / logout / switch).
  useEffect(() => {
    setState(readStored(userId));
  }, [userId]);

  // Auto-completion: merge signals into persisted completions. Signals
  // only add to the set — once a task is completed, it stays completed
  // even if the signal later flips back to false (rare: contact deleted
  // after import). This mirrors how checklist UIs in Linear / Vercel /
  // Stripe behave: "done once" not "currently satisfied".
  useEffect(() => {
    const signalMap: Record<ChecklistTaskId, boolean> = {
      connect_phone: signals.phoneSet,
      import_contacts: signals.hasContacts,
      create_campaign: signals.hasCampaigns,
      invite_teammate: signals.teamSizeGreaterThanOne,
      review_billing: signals.billingViewed,
    };
    setState((prev) => {
      const nextCompletions = { ...prev.completions };
      let changed = false;
      (Object.keys(signalMap) as ChecklistTaskId[]).forEach((id) => {
        if (signalMap[id] && !nextCompletions[id]) {
          nextCompletions[id] = true;
          changed = true;
        }
      });
      if (!changed) return prev;
      const next = { ...prev, completions: nextCompletions };
      writeStored(userId, next);
      return next;
    });
  }, [userId, signals.phoneSet, signals.hasContacts, signals.hasCampaigns, signals.teamSizeGreaterThanOne, signals.billingViewed]);

  const dismiss = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, dismissed: true };
      writeStored(userId, next);
      return next;
    });
  }, [userId]);

  const markCompleted = useCallback(
    (id: ChecklistTaskId) => {
      setState((prev) => {
        if (prev.completions[id]) return prev;
        const next = {
          ...prev,
          completions: { ...prev.completions, [id]: true },
        };
        writeStored(userId, next);
        return next;
      });
    },
    [userId],
  );

  const tasks = useMemo<ChecklistTask[]>(
    () => [
      {
        id: 'connect_phone',
        label: 'Connect your phone number',
        description: 'Add a callback number so we can route return calls to you.',
        to: '/settings/account',
        completed: Boolean(state.completions.connect_phone),
      },
      {
        id: 'import_contacts',
        label: 'Import your contacts',
        description: 'Upload a CSV or add contacts one by one.',
        to: '/contacts',
        completed: Boolean(state.completions.import_contacts),
      },
      {
        id: 'create_campaign',
        label: 'Create your first campaign',
        description: 'Pick an audience, set a retry strategy, and let the agent place the calls.',
        to: '/campaigns/create',
        completed: Boolean(state.completions.create_campaign),
      },
      {
        id: 'invite_teammate',
        label: 'Invite a teammate',
        description: 'Share a workspace so teammates can manage campaigns too.',
        to: '/settings/team',
        completed: Boolean(state.completions.invite_teammate),
      },
      {
        id: 'review_billing',
        label: 'Review your plan and credits',
        description: 'Understand what your current plan includes before scaling.',
        to: '/settings/billing',
        completed: Boolean(state.completions.review_billing),
      },
    ],
    [state.completions],
  );

  const completeCount = tasks.filter((t) => t.completed).length;
  const visible = !state.dismissed && completeCount < tasks.length;

  return {
    tasks,
    dismissed: state.dismissed,
    visible,
    completeCount,
    dismiss,
    markCompleted,
  };
}
